/**
 * Regras dos modelos (templates) de WhatsApp da campanha: nomes legíveis,
 * partes do modelo, comandos e o rascunho da mensagem da loja.
 *
 * Comandos (os mesmos do SMS): {nome} = primeiro nome do cadastro do cliente,
 * {empresa} = nome da loja, {link} = link rastreado da campanha. Na Meta cada
 * comando vira uma variável {{n}}; o servidor preenche cliente a cliente.
 */
import axios from "axios";

const auth = () => {
  const t = localStorage.getItem("token");
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

export const waApi = {
  modelos: () => axios.get("/api/whatsapp-broadcast/templates", auth()).then((r) => r.data?.templates || []),
  criarModelo: (body) => axios.post("/api/whatsapp-broadcast/templates", body, auth()).then((r) => r.data),
  publico: () => axios.post("/api/whatsapp-broadcast/preview", {}, auth()).then((r) => r.data),
  teste: (body) => axios.post("/api/whatsapp-broadcast/enviar-teste", body, auth()).then((r) => r.data),
  disparar: (body) => axios.post("/api/whatsapp-broadcast/disparar", body, auth()).then((r) => r.data),
  carteira: () => axios.get("/api/sms/carteira", auth()).then((r) => r.data),
  empresa: () => axios.get("/api/company/settings", auth()).then((r) => r.data),
  agendadas: () => axios.get("/api/whatsapp-broadcast/agendadas", auth()).then((r) => r.data?.agendadas || []),
  cancelar: (id) => axios.post(`/api/whatsapp-broadcast/agendadas/${id}/cancelar`, {}, auth()).then((r) => r.data),
};

/** Botões que a loja pode pôr na mensagem própria (limites da Meta/da tela). */
export const TIPOS_BOTAO = {
  link: { rotulo: "Link do cardápio", max: 1, padrao: "Ver cardápio" },
  ligar: { rotulo: "Ligar pra loja", max: 1, padrao: "Ligar pra loja" },
  resposta: { rotulo: "Resposta rápida", max: 3, padrao: "Quero!" },
};

export const PRECO_MSG = 0.399;
export const LINK_EXEMPLO = "usenucleo.com.br/c/k3Bx9";

/** Comandos prontos: o que cada um vira e como aparece na prévia. */
export const COMANDOS = {
  nome: { token: "{nome}", rotulo: "Nome do cliente", dica: "primeiro nome do cadastro de cada cliente" },
  empresa: { token: "{empresa}", rotulo: "Nome da loja", dica: "nome da sua loja" },
  link: { token: "{link}", rotulo: "Link rastreável", dica: "link da campanha: conta cliques e pedidos, igual ao SMS" },
};
const TOKEN_PARA_TIPO = Object.fromEntries(Object.entries(COMANDOS).map(([k, c]) => [c.token, k]));
export const RE_TOKENS = /(\{nome\}|\{empresa\}|\{link\})/g;

export const brl = (v) =>
  `R$ ${(Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const STATUS = {
  APPROVED: { rotulo: "Aprovado", cls: "text-emerald-600 bg-emerald-500/10" },
  PENDING: { rotulo: "Em análise na Meta", cls: "text-amber-600 bg-amber-500/10" },
  REJECTED: { rotulo: "Recusado pela Meta", cls: "text-red-600 bg-red-500/10" },
};

/** "nucleo_oferta_da_semana" → "Oferta da semana". */
export function nomeAmigavel(name) {
  const limpo = (name || "").replace(/^nucleo_/, "").replace(/_/g, " ").trim();
  return limpo ? limpo[0].toUpperCase() + limpo.slice(1) : "Sem nome";
}

/** "Oferta da Semana!" → "oferta_da_semana" (o nome técnico que a Meta aceita). */
export function nomeTecnico(titulo) {
  return (titulo || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

/** Aprovados primeiro, depois em análise e recusados; dentro de cada um, por nome. */
export function ordenarModelos(lista) {
  const peso = { APPROVED: 0, PENDING: 1, REJECTED: 2 };
  return [...lista].sort((a, b) =>
    (peso[a.status] ?? 3) - (peso[b.status] ?? 3) || nomeAmigavel(a.name).localeCompare(nomeAmigavel(b.name)));
}

/** Componentes da Meta → {cabecalho, corpo, rodape, botoes:[{tipo, texto, dinamico}]}. */
export function partesDoModelo(t) {
  const comp = (tipo) => (t?.components || []).find((c) => (c.type || "").toUpperCase() === tipo);
  const header = comp("HEADER");
  const TIPO = { URL: "link", PHONE_NUMBER: "ligar" };
  const botoes = (comp("BUTTONS")?.buttons || []).map((b) => ({
    tipo: TIPO[(b.type || "").toUpperCase()] || "resposta",
    texto: b.text,
    dinamico: (b.url || "").includes("{{1}}"),
  }));
  return {
    cabecalho: header?.format === "TEXT" ? header.text : header ? `[${(header.format || "mídia").toLowerCase()}]` : "",
    corpo: comp("BODY")?.text || "",
    rodape: comp("FOOTER")?.text || "",
    botoes,
  };
}

export function nVars(corpo) {
  const nums = [...(corpo || "").matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1]));
  return nums.length ? Math.max(...nums) : 0;
}

// Palavras logo antes do {{n}} que dizem o que ele é (modelo sem mapa salvo).
const PISTAS = [
  ["link", /(link|acesse|clique|pe[çc]a (em|aqui|pelo)|https?:?)\s*:?\s*$/],
  ["nome", /(ol[áa]|oi+e?|bom dia|boa tarde|boa noite|e a[íi]|fala|querid[oa]|prezad[oa])[\s,!]*$/],
  ["empresa", /\b(no|na|do|da|em|aqui (no|na)|equipe( do| da)?|time( do| da)?)\s*$/],
];

/**
 * O que preenche cada {{n}} de um modelo pronto: {tipo: nome|empresa|link|texto, texto}.
 * Modelo escrito na tela traz o mapa salvo. Nos outros, o texto em volta
 * decide ("Olá {{1}}" = nome do cliente, "no {{2}}" = nome da loja, "link
 * {{3}}" = link rastreável). Só o que não dá pra deduzir vira texto a completar.
 */
export function camposIniciais(modelo) {
  const corpo = partesDoModelo(modelo).corpo;
  const salvos = modelo?.variaveis || [];
  return Array.from({ length: nVars(corpo) }, (_, i) => {
    if (salvos[i]) return { tipo: salvos[i], texto: "" };
    const antes = corpo.split(`{{${i + 1}}}`)[0].toLowerCase().slice(-24);
    const achada = PISTAS.find(([, re]) => re.test(antes));
    return { tipo: achada ? achada[0] : "texto", texto: "" };
  });
}

/** Campo → valor mandado ao servidor (o comando ou o texto fixo). */
export const valorDoCampo = (c) => (c.tipo === "texto" ? c.texto : COMANDOS[c.tipo].token);

/** Confere o rascunho antes de mandar pra Meta. Devolve o problema ou null. */
export function problemaDoRascunho({ titulo, texto }) {
  const t = (texto || "").trim();
  if (!nomeTecnico(titulo)) return "Dê um nome pro modelo (ex.: Oferta da semana).";
  if (t.length < 15) return "Escreva a mensagem.";
  if (t.length > 1000) return "A mensagem passou de 1.000 caracteres.";
  const abre = t.match(/^\{(nome|empresa|link)\}/);
  const fecha = t.match(/\{(nome|empresa|link)\}$/);
  if (abre || fecha)
    return "A Meta não aceita um comando abrindo ou fechando a mensagem: escreva algo antes e depois (ex.: Oi {nome}, tudo bem? … Peça aqui: {link} Te esperamos!).";
  return null;
}

/** Rascunho da loja → corpo da criação do modelo (comandos viram {{1}}, {{2}}…). */
export function rascunhoParaMeta({ titulo, texto, botoes }, exemplos) {
  const variaveis = [];
  const corpo = texto.trim().replace(RE_TOKENS, (tok) => {
    variaveis.push(TOKEN_PARA_TIPO[tok]);
    return `{{${variaveis.length}}}`;
  });
  return {
    nome: nomeTecnico(titulo),
    corpo,
    variaveis,
    exemplos: variaveis.map((v) => exemplos[v]),
    opt_out: true,
    botoes: (botoes || []).map((b) => ({ tipo: b.tipo, texto: (b.texto || TIPOS_BOTAO[b.tipo].padrao).trim(), telefone: b.telefone })),
  };
}
